import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  HiXCircle,
  HiChartBar,
  HiClock,
  HiTrendingUp,
  HiLogout,
  HiChevronLeft,
  HiChevronRight,
  HiPhotograph,
  HiCog,
  HiChevronDown,
  HiLightBulb,
} from 'react-icons/hi';
import apiClient from '../api/client';
import ExcludedKeywords from '../ExcludedKeywords';
import CampaignStatistics from '../pages/CampaignStatistics';
import ChangeHistory from '../pages/ChangeHistory';
import SalesFunnelStatistics from '../pages/SalesFunnelStatistics';
import CreativeAbTests from '../pages/CreativeAbTests';
import RecommendationsPage from '../pages/RecommendationsPage';
import ClientCabinetSettings from '../pages/ClientCabinetSettings';
import OzonCampaignCabinet from '../pages/ozon/OzonCampaignCabinet';
import AvitoAdsCabinet from '../pages/avito/AvitoAdsCabinet';
import AvitoListingsOverview from '../pages/avito/AvitoListingsOverview';
import AvitoListingsPromo from '../pages/avito/AvitoListingsPromo';
import OzonSalesFunnelStatistics from '../pages/ozon/OzonSalesFunnelStatistics';
import BrandLogo from '../components/BrandLogo';
import { useClientMarketplaces } from '../hooks/useClientMarketplaces';
import { useWbNavigate, useWbPath } from './WbRouteContext';
import WbShell from './WbShell';
import { clearAuth } from '../utils/auth';

const NAV_WB = [
  { id: 'overview', label: 'Воронка продаж', icon: HiTrendingUp, desc: 'Карточки и конверсии' },
  { id: 'creative-ab', label: 'А/Б креативов', icon: HiPhotograph, desc: 'Тесты главного фото' },
  { id: 'campaigns', label: 'Реклама', icon: HiChartBar, desc: 'Кампании и расходы' },
  { id: 'recommendations', label: 'Рекомендации', icon: HiLightBulb, desc: 'Остатки, запуск и перелив бюджета' },
  { id: 'minusa', label: 'Минус-фразы', icon: HiXCircle, desc: 'Автоминусация' },
  { id: 'history', label: 'История изменений', icon: HiClock },
  { id: 'settings', label: 'Настройки', icon: HiCog, desc: 'API-ключи кабинета' },
];

const NAV_OZON = [
  { id: 'overview', label: 'Сводная статистика', icon: HiTrendingUp, desc: 'Аналитика Ozon' },
  { id: 'creative-ab', label: 'А/Б креативов', icon: HiPhotograph, desc: 'Тесты фото Ozon' },
  { id: 'campaigns', label: 'Реклама', icon: HiChartBar, desc: 'Кампании Ozon Performance' },
  { id: 'history', label: 'История изменений', icon: HiClock },
  { id: 'settings', label: 'Настройки', icon: HiCog, desc: 'API-ключи Ozon' },
];

const NAV_AVITO = [
  { id: 'overview', label: 'Объявления', icon: HiTrendingUp, desc: 'Статистика и эффективность объявлений' },
  { id: 'promo', label: 'Продвижение', icon: HiChartBar, desc: 'Ставки и биддер по объявлениям' },
  { id: 'history', label: 'История изменений', icon: HiClock },
  { id: 'settings', label: 'Настройки', icon: HiCog, desc: 'API-ключи Авито' },
];

const NAV_AVITO_ADS = [
  { id: 'campaigns', label: 'Кампании', icon: HiChartBar, desc: 'Avito Реклама и цели Метрики' },
  { id: 'history', label: 'История изменений', icon: HiClock },
  { id: 'settings', label: 'Настройки', icon: HiCog, desc: 'API-ключ Avito Реклама' },
];

const NAV_BY_MP = {
  wb: NAV_WB,
  ozon: NAV_OZON,
  avito: NAV_AVITO,
  avito_ads: NAV_AVITO_ADS,
};

const MP_META = {
  wb: { label: 'Wildberries', dot: 'bg-purple-500' },
  ozon: { label: 'Ozon', dot: 'bg-blue-500' },
  avito: { label: 'Авито', dot: 'bg-emerald-500' },
  avito_ads: { label: 'Avito Реклама', dot: 'bg-orange-500' },
};

function navForMarketplace(mp) {
  return NAV_BY_MP[mp] || NAV_WB;
}

export default function WbAppWithTabs({ clientId }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [collapsed, setCollapsed] = useState(false);
  const [clientName, setClientName] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const navigate = useWbNavigate();
  const appPath = useWbPath();
  const { caps, marketplace, setMarketplace, loading: capsLoading } = useClientMarketplaces(clientId);

  const sections = useMemo(() => {
    const list = caps?.marketplaces?.length ? caps.marketplaces : ['wb'];
    return list.map((mp) => ({
      id: mp,
      ...(MP_META[mp] || { label: mp, dot: 'bg-gray-400' }),
      items: navForMarketplace(mp),
    }));
  }, [caps]);

  const activeItem = useMemo(
    () => navForMarketplace(marketplace).find((i) => i.id === activeTab),
    [marketplace, activeTab],
  );

  useEffect(() => {
    if (!sections.length) return;
    const items = navForMarketplace(marketplace);
    if (!items.some((i) => i.id === activeTab)) {
      setActiveTab(items[0]?.id || 'overview');
    }
  }, [marketplace, sections.length, activeTab]);

  useEffect(() => {
    if (!sections.length) return;
    setExpandedSections((prev) => {
      const next = { ...prev };
      sections.forEach((s, idx) => {
        if (next[s.id] === undefined) {
          next[s.id] = s.id === marketplace || (idx === 0 && !marketplace);
        }
      });
      if (marketplace) next[marketplace] = true;
      return next;
    });
  }, [sections, marketplace]);

  useEffect(() => {
    if (!clientId) {
      setClientName('');
      return;
    }
    let cancelled = false;
    apiClient.get('/clients').then(({ data }) => {
      if (cancelled) return;
      const client = Array.isArray(data) ? data.find((c) => String(c.id) === String(clientId)) : null;
      setClientName(client?.name || '');
    }).catch(() => { if (!cancelled) setClientName(''); });
    return () => { cancelled = true; };
  }, [clientId]);

  const handleSelectTab = useCallback((mp, tabId) => {
    setMarketplace(mp);
    setActiveTab(tabId);
    setExpandedSections((prev) => ({ ...prev, [mp]: true }));
  }, [setMarketplace]);

  const toggleSection = useCallback((mp) => {
    setExpandedSections((prev) => ({ ...prev, [mp]: !prev[mp] }));
  }, []);

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const renderContent = () => {
    if (activeTab === 'settings') return <ClientCabinetSettings clientId={clientId} />;

    if (marketplace === 'wb') {
      if (activeTab === 'overview') return <SalesFunnelStatistics clientId={clientId} />;
      if (activeTab === 'creative-ab') return <CreativeAbTests clientId={clientId} />;
      if (activeTab === 'campaigns') return <CampaignStatistics clientId={clientId} />;
      if (activeTab === 'recommendations') return <RecommendationsPage clientId={clientId} />;
      if (activeTab === 'minusa') return <ExcludedKeywords clientId={clientId} />;
      if (activeTab === 'history') return <ChangeHistory clientId={clientId} />;
    }

    if (marketplace === 'ozon') {
      if (activeTab === 'overview') return <OzonSalesFunnelStatistics clientId={clientId} />;
      if (activeTab === 'creative-ab') return <CreativeAbTests clientId={clientId} marketplace="ozon" />;
      if (activeTab === 'campaigns') return <OzonCampaignCabinet clientId={clientId} />;
      if (activeTab === 'history') return <ChangeHistory clientId={clientId} />;
    }

    if (marketplace === 'avito') {
      if (activeTab === 'overview') return <AvitoListingsOverview clientId={clientId} />;
      if (activeTab === 'promo') return <AvitoListingsPromo clientId={clientId} />;
      if (activeTab === 'history') return <ChangeHistory clientId={clientId} />;
    }

    if (marketplace === 'avito_ads') {
      if (activeTab === 'campaigns') return <AvitoAdsCabinet clientId={clientId} />;
      if (activeTab === 'history') return <ChangeHistory clientId={clientId} />;
    }

    return null;
  };

  return (
    <WbShell banner={false}>
      <div className="flex min-h-screen">
        <aside className={`wb-sidebar relative ${collapsed ? 'w-[72px]' : 'w-64'}`} aria-label="Навигация">
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="absolute -right-3 top-6 z-10 w-6 h-6 rounded-full bg-[var(--wb-surface-raised)] border border-[var(--wb-border)] shadow-sm flex items-center justify-center"
            aria-label={collapsed ? 'Развернуть' : 'Свернуть'}
          >
            {collapsed ? <HiChevronRight className="w-3.5 h-3.5" /> : <HiChevronLeft className="w-3.5 h-3.5" />}
          </button>

          <div className={`p-4 border-b border-[var(--wb-border)] ${collapsed ? 'px-3' : ''}`}>
            <Link to={appPath('/dashboard')} className="flex items-center gap-3 rounded-xl p-1 -m-1 hover:bg-[var(--wb-surface-muted)]">
              <BrandLogo size={40} className="shadow-md ring-1 ring-white/30" />
              {!collapsed && (
                <div className="min-w-0">
                  <p className="font-bold text-sm tracking-tight">ТОРГОСЛАВ</p>
                  <p className="text-xs text-[var(--wb-text-muted)] truncate">{clientName || 'Кабинет'}</p>
                </div>
              )}
            </Link>
          </div>

          <nav className="flex-1 p-3 space-y-2 overflow-y-auto">
            {sections.map((section) => {
              const isOpen = Boolean(expandedSections[section.id]);
              const isActiveSection = marketplace === section.id;

              if (collapsed) {
                return (
                  <button
                    key={section.id}
                    type="button"
                    title={section.label}
                    onClick={() => {
                      setCollapsed(false);
                      setExpandedSections((prev) => ({ ...prev, [section.id]: true }));
                      if (!isActiveSection) {
                        handleSelectTab(section.id, section.items[0]?.id || 'overview');
                      }
                    }}
                    className={`wb-nav-item justify-center px-2 ${isActiveSection ? 'wb-nav-item--active' : ''}`}
                  >
                    <span className={`wb-mp-dot ${section.dot}`} />
                  </button>
                );
              }

              return (
                <div key={section.id} className="wb-nav-group">
                  <button
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    className={`wb-nav-group-header ${isActiveSection ? 'wb-nav-group-header--active' : ''}`}
                    aria-expanded={isOpen}
                  >
                    <span className={`wb-mp-dot ${section.dot}`} />
                    <span className="truncate">{section.label}</span>
                    <HiChevronDown
                      className={`wb-nav-group-chevron ${isOpen ? 'wb-nav-group-chevron--open' : ''}`}
                    />
                  </button>

                  {isOpen && (
                    <div className="wb-nav-subitems">
                      {section.items.map((item) => {
                        const isActive = isActiveSection && activeTab === item.id;
                        return (
                          <button
                            key={`${section.id}-${item.id}`}
                            type="button"
                            onClick={() => handleSelectTab(section.id, item.id)}
                            className={`wb-nav-subitem ${isActive ? 'wb-nav-subitem--active' : ''}`}
                          >
                            <item.icon className="w-4 h-4 shrink-0 opacity-80" />
                            <span className="truncate">{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className={`p-3 border-t border-[var(--wb-border)] space-y-1 ${collapsed ? 'px-2' : ''}`}>
            {!collapsed && (
              <Link to={appPath('/dashboard')} className="wb-nav-item text-sm">← Все кабинеты</Link>
            )}
            <button
              type="button"
              onClick={handleLogout}
              className={`wb-nav-item text-[var(--wb-danger)] hover:bg-[var(--wb-danger-bg)] ${collapsed ? 'justify-center px-2' : ''}`}
            >
              <HiLogout className="w-5 h-5 shrink-0" />
              {!collapsed && <span>Выйти</span>}
            </button>
          </div>
        </aside>

        <div className="flex-1 flex flex-col min-w-0 wb-content-area">
          <header className="wb-content-header">
            <p className="text-xs font-medium text-[var(--wb-text-muted)] mb-0.5">
              {MP_META[marketplace]?.label || marketplace}
            </p>
            <h1 className="text-lg font-semibold">{activeItem?.label}</h1>
            {activeItem?.desc && (
              <p className="text-sm text-[var(--wb-text-muted)] mt-0.5">{activeItem.desc}</p>
            )}
          </header>

          <main id="main-content" className="wb-content-main">
            {capsLoading ? (
              <p className="text-[var(--wb-text-muted)]">Загрузка кабинета…</p>
            ) : (
              renderContent()
            )}
          </main>
        </div>
      </div>
    </WbShell>
  );
}
